export interface LangfuseDataset {
  id?: string;
  name: string;
  description?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
  item_count?: number;
}

export interface LangfuseDatasetDetail {
  id?: string;
  name: string;
  description?: string;
  metadata?: Record<string, unknown>;
  items: LangfuseDatasetItem[];
  item_count: number;
}

export const PERSONA_AGE_RANGES = ['20-30', '30-40', '40-50', '50-60', '60+'] as const;
export type PersonaAgeRange = (typeof PERSONA_AGE_RANGES)[number];

export interface StructuredPersona {
  name: string;
  title: string;
  age_range: PersonaAgeRange;
  company: string;
  industry: string;
  goal: string;
  pain_points: string;
  constraints: string;
  [key: string]: unknown;
}

export type PersonaInput = StructuredPersona;

export function isStructuredPersona(input: PersonaInput | undefined | null): input is StructuredPersona {
  if (!input || typeof input !== 'object') return false;
  const candidate = input as Partial<StructuredPersona>;
  return typeof candidate.name === 'string' && candidate.name.trim().length > 0
    && typeof candidate.title === 'string' && candidate.title.trim().length > 0;
}

export interface LangfuseDatasetItem {
  id?: string;
  input: PersonaInput;
  expected_output?: string;
  metadata?: Record<string, unknown>;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export type PopulationKind = 'representative' | 'targeted';

export interface PersonaGenerateRequest {
  dataset_name: string;
  num_personas: number;
  user_id?: string;
  organization_id: string;
  population_kind?: PopulationKind;
  description?: string;
}

export interface PersonaGenerateResponse {
  success: boolean;
  dataset_id?: string;
  dataset_name: string;
  items_created: number;
  items: StructuredPersona[];
  message: string;
}

export interface DatasetItemUpdateRequest {
  input: Record<string, unknown>;
}

export interface SimulationBatchCreateRequest {
  name: string;
  organization_id: string;
  prompt_name: string;
  prompt_version?: number | null;
  dataset_names: string[];
  user_id?: string | null;
}

export interface SimulationBatchRunRequest {
  organization_id: string;
  prompt_version?: number | null;
  experiment_name?: string;
  max_turns?: number;
  use_single_shot?: boolean;
  max_concurrency?: number;
  user_id?: string | null;
}

export interface SimulationBatchRunResponse {
  batch_id: string;
  status: string;
  completed: number;
  total: number;
  runs?: SimulationRunSummary[];
}

export type EvaluatorView = 'home' | 'dataset' | 'item';

export interface SimulationRunSummary {
  run_id: string;
  dataset_name: string;
  status: string;
  trace_ids: string[];
  run_name?: string | null;
  metrics?: Record<string, unknown> | null;
  prompt_version?: string | null;
  agent_version?: string | null;
}

export interface SimulationConversation {
  id: string;
  run_id: string;
  dataset_name: string;
  status: string;
  input_vars: Record<string, unknown>;
  output?: string | null;
  scores?: Record<string, unknown> | null;
  trace_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
}

export interface SimulationBatch {
  id: string;
  organization_id: string;
  name: string;
  prompt_name: string;
  prompt_version?: string | null;
  agent_version?: string | null;
  dataset_names: string[];
  status: string;
  aggregate_metrics?: Record<string, unknown> | null;
  optimization_run_id?: string | null;
  runs?: SimulationRunSummary[];
  conversations?: SimulationConversation[];
  created_by_user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SimulationBatchListResponse {
  batches: SimulationBatch[];
  total: number;
}
